// src/components/pages/map/PinDetailsPage.js
import React, { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { Link as RouterLink, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  CircularProgress,
  Avatar,
  Typography,
  Button,
  Divider,
  Chip,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import ShareIcon from "@mui/icons-material/Share";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import DirectionsIcon from "@mui/icons-material/Directions";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import PlaceIcon from "@mui/icons-material/Place";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import SpaIcon from "@mui/icons-material/Spa";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIcon from "@mui/icons-material/Phone";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import IconMarkerPublic from "../../../assets/icons/v2/map-pin-public.webp";
import IconSeededPublicCapsule from "../../../assets/icons/v2/map-pin-seeded.webp";
import IconSentCapsule from "../../../assets/icons/v2/time-capsule-sent.webp";
import IconTimeCapsuleMemory from "../../../assets/icons/v2/time-capsule-memory.webp";
import IconTimeCapsuleClosedMarker from "../../../assets/icons/v2/time-capsule-closed.webp";
import IconTimeCapsuleLockedMarker from "../../../assets/icons/v2/time-capsule-locked.webp";
import IconTimeCapsuleOpenedMarker from "../../../assets/icons/v2/time-capsule-opened.webp";
import IconTreasureClosedMarker from "../../../assets/icons/v2/treasure-box-locked.webp";
import IconTreasureOpenedMarker from "../../../assets/icons/v2/treasure-box-unlocked.webp";
import IconInsiderStar from "../../../assets/icons/v2/insider-star.webp";
import Countdown from "react-countdown";
import { useCapsuleDetails } from "../../../hooks/useCapsuleDetails";
import { messageToBeOppendText, formatDuration } from "../../../utils/commonFunctions";
import { MediaSlider } from "../../common/MediaSlider";
import { LightboxModal } from "../../common/LightboxModal";
import { SentToDisplay } from "../../common/SentToDisplay";
import { CapsuleThread } from "../../common/CapsuleThread";
import VideoAdModal from "../../VideoAdModal";
import { db, auth } from "../../../firebase";
import {
  logCapsuleEvent,
  logCapsuleFollow,
  isMeasurableCapsule,
  newShareId,
} from "../../../utils/measurement";

import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { resolveClaimPathOffer } from "../../../utils/nearbyDiscovery";
import { getProfilePathByUid } from "../../../utils/publicProfile";
import { ellipsizeProfileText } from "../../../utils/profileText";
import {
  fetchUserProfileFromFirestore,
  getLovedTimeCapsuleIds,
  setLovedTimeCapsule,
} from "../../../services/profileService";
import useUserProfileStore, {
  selectSetUserProfile,
  selectUserSubscriptionData,
  selectUserSubscriptionTier,
} from "../../../store/useUserProfileStore";
import { getCapsuleDisplayLabel } from "../../../utils/capsuleDisplay";
import { buildAvatarImageAttrs } from "../../../utils/imageUrl";
import {
  resolveSponsoredAvatar,
} from "../../../services/sponsoredAvatarService";
import {
  fetchBusinessById,
  followBusiness,
  isFollowingBusiness,
  unfollowBusiness,
} from "../../../services/businessService";
import {
  hasGetUserMedia,
  isIOS,
  isMobileUserAgent,
  isSecureContextSafe,
} from "../../../utils/platform";
import { getDeviceNavigationUrl } from "../../../utils/navigationApps";
import { useSeoRoute } from "../../../seo";
import { useSessionCapabilities } from "../../../hooks/useSessionCapabilities";
import { hasInsiderAccess, shouldGateMemberExclusiveContent } from "../../../utils/insiderAccess";
import { buildFindQuickTake } from "../../../utils/findQuickTake";
import { buildPlaceVisitInfo } from "../../../utils/placeVisitInfo";
import {
  buildForwardWizardCapsule,
  getForwardShareMode,
  isFeaturedForwardCapsule,
} from "../../../utils/timeCapsuleForward";
import useForwardedFeaturedSource from "../../../hooks/useForwardedFeaturedSource";
import ForwardedFeaturedSection from "./ForwardedFeaturedSection";
import SuccessDialog from "../../SuccessDialog";
import {
  buildCapsuleShareMessage,
  getShareCopyVariant,
} from "../../../utils/shareCopy";

function maskPhone(phone) {
  if (typeof phone !== "string") return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return "";
  return `(***) ***-${digits.slice(-4)}`;
}

function maskEmail(email) {
  if (typeof email !== "string") return "";
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.indexOf("@");
  if (atIndex <= 0) return "";

  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  if (!domain) return "";

  const maskedLocal =
    local.length <= 2
      ? `${local[0] || ""}*`
      : `${local[0]}${"*".repeat(local.length - 2)}${local[local.length - 1]}`;

  return `${maskedLocal}@${domain}`;
}

function formatPublicHandle(value) {
  const normalized = String(value || "").trim().replace(/^@+/, "");
  return normalized ? `@${normalized}` : "";
}

function normalizeRoleList(input) {
  if (Array.isArray(input)) {
    return input
      .map((value) => String(value || "").trim().toLowerCase())
      .filter(Boolean);
  }

  if (typeof input === "string") {
    const trimmed = input.trim().toLowerCase();
    return trimmed ? [trimmed] : [];
  }

  if (input && typeof input === "object") {
    return Object.entries(input)
      .filter(([, enabled]) => enabled === true)
      .map(([role]) => String(role || "").trim().toLowerCase())
      .filter(Boolean);
  }

  return [];
}

function computeSenderLabel(user) {
  const displayName = typeof user?.displayName === "string" ? user.displayName.trim() : "";
  if (displayName) return ellipsizeProfileText(displayName);

  const userNameRaw = typeof user?.userName === "string" ? user.userName.trim() : "";
  if (userNameRaw) {
    const atIndex = userNameRaw.indexOf("@");
    const userNameDisplay =
      atIndex > 0 ? userNameRaw.slice(0, atIndex).trim() : userNameRaw;
    if (userNameDisplay) return ellipsizeProfileText(userNameDisplay);
  }

  const maskedPhone = maskPhone(user?.phoneNumber);
  if (maskedPhone) return maskedPhone;

  const maskedEmail = maskEmail(user?.email);
  if (maskedEmail) return maskedEmail;

  return ellipsizeProfileText("Connection");
}

function getInitials(label) {
  if (!label || typeof label !== "string") return "C";
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function joinPreviewParts(parts) {
  const cleanParts = parts.filter(Boolean);
  if (cleanParts.length === 0) return "A time capsule is waiting";
  if (cleanParts.length === 1) {
    return cleanParts[0].startsWith("1 ") ? `${cleanParts[0]} is waiting` : `${cleanParts[0]} are waiting`;
  }
  if (cleanParts.length === 2) return `${cleanParts[0]} and ${cleanParts[1]} are waiting`;
  return `${cleanParts.slice(0, -1).join(", ")}, and ${cleanParts[cleanParts.length - 1]} are waiting`;
}

function buildPreviewInventorySummary(inventory = {}) {
  const mediaCount = Math.max(0, Number(inventory.mediaCount) || 0);
  const parts = [];
  if (mediaCount > 0) parts.push(pluralize(mediaCount, "memory", "memories"));
  if (inventory.hasPlace) parts.push("1 place");
  if (inventory.hasNote) parts.push("1 note");
  return joinPreviewParts(parts);
}

function normalizePreviewMediaType(type) {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized.startsWith("video")) return "video";
  if (normalized.startsWith("image") || normalized === "photo") return "image";
  return "media";
}

function getPreviewMediaLabel(type) {
  const normalized = normalizePreviewMediaType(type);
  if (normalized === "video") return "Video locked";
  if (normalized === "image") return "Photo locked";
  return "Memory locked";
}

function getPreviewMediaBackground(type, theme) {
  const normalized = normalizePreviewMediaType(type);
  if (normalized === "video") {
    return `
      radial-gradient(circle at 25% 24%, ${alpha(theme.palette.common.white, 0.64)}, transparent 12%),
      radial-gradient(circle at 68% 70%, ${alpha(theme.colors.blue, 0.46)}, transparent 20%),
      linear-gradient(135deg, #5268a4, #935e92 49%, #ce9466)
    `;
  }
  if (normalized === "image") {
    return `
      radial-gradient(circle at 24% 28%, ${alpha(theme.palette.common.white, 0.7)}, transparent 13%),
      radial-gradient(circle at 70% 66%, ${alpha(theme.palette.common.black, 0.48)}, transparent 20%),
      linear-gradient(135deg, #2d5871, #7d688e 48%, #dab677)
    `;
  }
  return `
    radial-gradient(circle at 30% 25%, ${alpha(theme.palette.common.white, 0.54)}, transparent 14%),
    linear-gradient(135deg, #46546f, #77658a 48%, #9b8465)
  `;
}

function getPreviewMediaUrl(mediaItem) {
  return String(
    mediaItem?.previewUrl ||
      mediaItem?.thumbnailUrl ||
      mediaItem?.posterUrl ||
      mediaItem?.imageUrl ||
      mediaItem?.photoUrl ||
      ""
  ).trim();
}

function toCssUrl(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return `url(${JSON.stringify(normalized)})`;
}

function buildPreviewMediaTiles(capsule = {}) {
  const previewMedia = Array.isArray(capsule.previewMedia) ? capsule.previewMedia : [];
  if (previewMedia.length) return previewMedia.slice(0, 4);
  const inventory = capsule.previewInventory || {};
  const types = Array.isArray(inventory.mediaTypes) ? inventory.mediaTypes : [];
  const mediaCount = Math.max(0, Number(inventory.mediaCount) || 0);
  if (mediaCount <= 0) return [];
  if (types.length) {
    return Array.from({ length: Math.min(mediaCount, 4) }, (_, index) => ({
      type: types[index % types.length],
    }));
  }
  return Array.from({ length: Math.min(mediaCount, 4) }, () => ({ type: "media" }));
}

function parseRevealDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") {
    const timestampDate = value.toDate();
    return Number.isNaN(timestampDate?.getTime?.()) ? null : timestampDate;
  }
  if (Number.isFinite(value?.seconds)) {
    const secondsDate = new Date(Number(value.seconds) * 1000);
    return Number.isNaN(secondsDate.getTime()) ? null : secondsDate;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseLatLngFromCapsule(capsule) {
  if (!capsule || typeof capsule !== "object") return null;
  const location = capsule.location;
  if (
    location &&
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lng)
  ) {
    return { lat: location.lat, lng: location.lng };
  }
  const position = capsule.position;
  if (
    position &&
    Number.isFinite(position.lat) &&
    Number.isFinite(position.lng)
  ) {
    return { lat: position.lat, lng: position.lng };
  }
  if (Number.isFinite(capsule.lat) && Number.isFinite(capsule.lng)) {
    return { lat: capsule.lat, lng: capsule.lng };
  }
  return null;
}

function resolveUnlockingCapsuleIcon(capsule, uid) {
  const capsuleType = String(capsule?.capsuleType || capsule?.type || "").trim().toLowerCase();
  const senderId = String(capsule?.senderId || "").trim();
  const existingIcon = String(capsule?.icon || "").trim();

  if (capsule?.insider === true) {
    return IconInsiderStar;
  }

  if (capsuleType === "sponsored") {
    return String(
      (existingIcon && existingIcon !== IconMarkerPublic ? existingIcon : "") ||
      capsule?.businessAvatarUrl ||
        capsule?.pinUrl ||
        capsule?.business?.pinUrl ||
        capsule?.business?.avatarUrl ||
        capsule?.business?.logoUrl ||
        capsule?.senderProfileImage ||
        ""
    ).trim() || IconSeededPublicCapsule;
  }

  if (capsuleType === "memberexclusive" || capsuleType === "member-exclusive") {
    if (existingIcon) return existingIcon;
    return capsule?.isSubscribedUser === true || capsule?.isUnlocked === true
      ? IconTreasureOpenedMarker
      : IconTreasureClosedMarker;
  }

  if (capsuleType === "private") {
    if (capsule?.isSelfCapsule === true || capsule?.audience === "self") {
      return IconTimeCapsuleMemory;
    }
    if (uid && senderId && senderId === String(uid)) {
      return IconSentCapsule;
    }

    const revealDate = parseRevealDate(capsule?.revealDate);
    const isLocked = Boolean(revealDate && revealDate > new Date());
    if (isLocked) return IconTimeCapsuleLockedMarker;

    const readBy = Array.isArray(capsule?.readBy) ? capsule.readBy : [];
    return uid && readBy.includes(uid) ? IconTimeCapsuleOpenedMarker : IconTimeCapsuleClosedMarker;
  }

  if (capsuleType === "public") {
    if (existingIcon) return existingIcon;
    return capsule?.seeded === true ? IconSeededPublicCapsule : IconMarkerPublic;
  }

  return IconTimeCapsuleClosedMarker;
}

const HIDDEN_MAP_CAPSULE_IDS_KEY = "inssa:hidden-map-capsule-ids";
const SEED_TYPE_ICON_BY_KEY = {
  see: PlaceIcon,
  eat: RestaurantIcon,
  drink: LocalBarIcon,
  do: SportsEsportsIcon,
  shop: ShoppingBagIcon,
  relax: SpaIcon,
};

// A capsule past its endDate is a normal lifecycle state, not a failure: the
// business scheduled it to stop. It gets its own copy so visitors aren't told
// the link is broken, and no Retry, because retrying can never bring it back.
function isCapsuleEndedError(error) {
  return String(error?.code || "").toLowerCase() === "capsule-ended";
}

function getCapsuleLoadErrorMessage(error, hasShareToken) {
  const code = String(error?.code || "").toLowerCase();
  if (isCapsuleEndedError(error)) {
    return "This time capsule has ended and is no longer available.";
  }
  if (hasShareToken && (code.includes("permission-denied") || code.includes("not-found"))) {
    return "This shared link is invalid, expired, or no longer available.";
  }
  if (hasShareToken && code.includes("failed-precondition")) {
    return "This link cannot open this type of capsule.";
  }
  if (hasShareToken) {
    return "We could not verify this shared link. Please try again.";
  }
  return "Capsule not found.";
}

const PinDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const setStoredUserProfile = useUserProfileStore(selectSetUserProfile);
  const subscriptionData = useUserProfileStore(selectUserSubscriptionData);
  const subscriptionTier = useUserProfileStore(selectUserSubscriptionTier);
  const shareToken = new URLSearchParams(location.search).get("token") || "";
  const {
    uid,
    capsule,
    senderUser,
    usersList,
    loading,
    loadingSender,
    error: capsuleLoadError,
    loadPhase,
    claimStatus,
    claimError,
    sharedAccessLevel,
    repairSeededPlaceMedia,
  } =
    useCapsuleDetails(id, shareToken);
  const sessionCapabilities = useSessionCapabilities();
  const [showSlowLoadHint, setShowSlowLoadHint] = useState(false);
  const [showExtendedLoadHint, setShowExtendedLoadHint] = useState(false);

  // Insider-activation success celebration. When the membership flow returns
  // the user here (returnTo=/capsule/:id after free Insider activation), it
  // carries the success payload in history state — show it once, then clear the
  // flag so a reload doesn't retrigger it. Mirrors HomePage's handling so the
  // celebration survives even though the destination changed from home to the
  // originating capsule.
  const [insiderSuccessOpen, setInsiderSuccessOpen] = useState(false);
  const [insiderSuccessPayload, setInsiderSuccessPayload] = useState(null);
  const [insiderSuccessKey, setInsiderSuccessKey] = useState(null);

  useEffect(() => {
    if (!location.state?.showSuccess) return;
    setInsiderSuccessOpen(true);
    setInsiderSuccessPayload(location.state.successDialog ?? null);
    setInsiderSuccessKey(location.state.successDialogKey ?? null);
    navigate(".", {
      replace: true,
      state: {
        ...location.state,
        showSuccess: false,
        successDialog: null,
        successDialogKey: null,
      },
    });
  }, [location.state, navigate]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const handleClose = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [viewerProfile, setViewerProfile] = useState(null);
  const [viewerProfileLoaded, setViewerProfileLoaded] = useState(false);
  const [viewerProfileLoadFailed, setViewerProfileLoadFailed] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [sponsoredAvatarUrl, setSponsoredAvatarUrl] = useState("");
  const [sponsoredBusiness, setSponsoredBusiness] = useState(null);
  const [isFollowingSponsoredBusiness, setIsFollowingSponsoredBusiness] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const targetLatLng = useMemo(() => parseLatLngFromCapsule(capsule), [capsule]);
  const placeVisitInfo = useMemo(
    () => buildPlaceVisitInfo(capsule?.placeFacts),
    [capsule?.placeFacts]
  );
  const hasPlaceVisitInfo =
    placeVisitInfo.facts.length ||
    placeVisitInfo.actions.length ||
    Boolean(placeVisitInfo.openStatus) ||
    Boolean(placeVisitInfo.hours);
  const viewerDisplayName = String(
    viewerProfile?.displayName ||
      auth.currentUser?.displayName ||
      ""
  ).trim();
  const quickTake = useMemo(
    () =>
      buildFindQuickTake({
        messageBody: capsule?.messageBody,
        displayName: viewerDisplayName,
        seedType: capsule?.seedType,
        placeFacts: capsule?.placeFacts,
      }),
    [capsule?.messageBody, capsule?.placeFacts, capsule?.seedType, viewerDisplayName]
  );
  const arSupported = useMemo(
    () => isIOS() && isMobileUserAgent() && isSecureContextSafe() && hasGetUserMedia(),
    []
  );

  // More menu + confirm dialog
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const moreOpen = Boolean(moreAnchorEl);
  const openMore = (e) => setMoreAnchorEl(e.currentTarget);
  const closeMore = () => setMoreAnchorEl(null);

  const [confirmAction, setConfirmAction] = useState(null);
  const [shareBlockedDialogOpen, setShareBlockedDialogOpen] = useState(false);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [insiderOverride, setInsiderOverride] = useState(null);

  const actionBarHeight = 73; // 48px controls + 24px vertical padding + 1px top border
  const persistentCtaBarHeight = 72;
  const persistentCtaGap = 0;
  const actionBarBottom = "env(safe-area-inset-bottom, 0px)";
  const redirectToSignIn = (nextPath) => {
    const defaultPath = `${location.pathname}${location.search || ""}`;
    const next = encodeURIComponent(nextPath || defaultPath);
    navigate(`/signin?next=${next}`, { state: { from: location } });
  };

  const flags = useMemo(() => {
    const isPrivate = capsule?.capsuleType === "private";
    return { isPrivate };
  }, [capsule]);
  const isPublicCapsule = String(capsule?.capsuleType || "").toLowerCase() === "public";
  const isSponsoredCapsule = String(capsule?.capsuleType || "").toLowerCase() === "sponsored";
  const isMemberExclusiveCapsule = String(capsule?.capsuleType || "").toLowerCase() === "memberexclusive";
  const isPrivateCapsule = String(capsule?.capsuleType || "").toLowerCase() === "private";
  // Sponsored and memberExclusive are the same product — a business-authored featured
  // drop. They present identically; the Insider gate below is the only difference.
  const isFeaturedCapsule = isSponsoredCapsule || isMemberExclusiveCapsule;
  // Sponsored carries a business by contract, so it brands eagerly. A memberExclusive
  // drop can also be creator-authored, so it only takes the business treatment once a
  // business document has actually resolved — otherwise the follow prompt and the
  // /business link would dead-end.
  const showsBusinessIdentity = isSponsoredCapsule || (isMemberExclusiveCapsule && Boolean(sponsoredBusiness));
  // Forwarded featured envelope: resolve the original featured capsule
  // (self-gating — idle unless this capsule references a featured source).
  const isForwardedFeaturedEnvelope = isFeaturedForwardCapsule(capsule);
  const forwardedFeatured = useForwardedFeaturedSource(capsule);
  const isForwardedFeaturedGated =
    forwardedFeatured.status === "available" &&
    shouldGateMemberExclusiveContent(
      forwardedFeatured.sourceCapsule,
      subscriptionData || viewerProfile,
      subscriptionTier
    );
  const forwardedFeaturedCta =
    forwardedFeatured.status === "available" && !isForwardedFeaturedGated
      ? {
          text: String(forwardedFeatured.sourceCapsule?.callToActionText || "").trim(),
          url: String(forwardedFeatured.sourceCapsule?.callToActionUrl || "").trim(),
        }
      : { text: "", url: "" };
  const callToActionText =
    String(capsule?.callToActionText || "").trim() || forwardedFeaturedCta.text;
  const callToActionUrl =
    String(capsule?.callToActionUrl || "").trim() || forwardedFeaturedCta.url;
  const hasCallToAction = Boolean(callToActionText && callToActionUrl);
  const shouldShowPersistentCta =
    hasCallToAction &&
    (isFeaturedCapsule || Boolean(forwardedFeaturedCta.text && forwardedFeaturedCta.url));
  const shouldShowInlineCta =
    !isPublicCapsule && hasCallToAction && !shouldShowPersistentCta;
  const contentBottomPadding = shouldShowPersistentCta
    ? actionBarHeight + persistentCtaGap + persistentCtaBarHeight + 16
    : actionBarHeight + 16;
  const videoAdAnchorOffset = shouldShowPersistentCta
    ? actionBarHeight + persistentCtaGap + persistentCtaBarHeight
    : actionBarHeight;
  const adAnchorBottom = `calc(${actionBarBottom} + ${videoAdAnchorOffset}px)`;
  const forwardShareMode = getForwardShareMode(capsule);
  const isEditableForwardCapsule = forwardShareMode === "editable";
  const isAlreadyInsiderPromoted =
    insiderOverride !== null ? insiderOverride : capsule?.insider === true || capsule?.adminStarred === true;
  const canToggleInsider =
    Boolean(uid) && isAdminUser && !isPrivateCapsule && Boolean(capsule?.id);

  useSeoRoute(
    capsule
      ? {
          id: capsule.id || id,
          title:
            capsule.title ||
            capsule.headline ||
            capsule.placeName ||
            capsule.seedName ||
            "Time Capsule",
          description: capsule.description || capsule.message || "",
          image:
            (Array.isArray(capsule.mediaUrls) && capsule.mediaUrls[0]) ||
            capsule.image ||
            capsule.posterUrl ||
            "",
          mediaUrls: capsule.mediaUrls || [],
          capsuleType: capsule.capsuleType || "private",
          createdAt: capsule.createdAt?.toDate?.()?.toISOString?.() || "",
          updatedAt: capsule.updatedAt?.toDate?.()?.toISOString?.() || "",
          authorName: senderUser?.displayName || senderUser?.userName || "",
        }
      : { id }
  );
  React.useEffect(() => {
    let cancelled = false;
    const loadSponsoredAvatar = async () => {
      if (!isFeaturedCapsule) {
        setSponsoredAvatarUrl("");
        setSponsoredBusiness(null);
        return;
      }
      const resolvedBusinessId = capsule?.businessId || capsule?.senderId;
      const business = await fetchBusinessById(resolvedBusinessId);
      if (!cancelled) {
        const businessAvatarUrl = String(
          business?.avatarUrl ||
            business?.logoUrl ||
            business?.profileImage ||
            business?.photoURL ||
            business?.pinUrl ||
            ""
        ).trim();
        setSponsoredAvatarUrl(businessAvatarUrl);
        setSponsoredBusiness(business || null);
      }
    };

    loadSponsoredAvatar();
    return () => {
      cancelled = true;
    };
  }, [capsule?.businessId, capsule?.senderId, isFeaturedCapsule]);

  // open — the capsule detail was opened. A DISTINCT client event (never piggy-
  // backed on readBy), fired once per capsule per mount for measurable campaigns.
  const openLoggedRef = React.useRef("");
  React.useEffect(() => {
    const cid = String(capsule?.id || "").trim();
    if (!cid || openLoggedRef.current === cid) return;
    if (!isMeasurableCapsule(capsule)) return;
    openLoggedRef.current = cid;
    logCapsuleEvent("open", capsule, { token: shareToken || undefined });
  }, [capsule, shareToken]);

  const senderAvatarUrl = useMemo(
    () =>
      resolveSponsoredAvatar({
        capsuleType: showsBusinessIdentity ? "sponsored" : capsule?.capsuleType,
        businessAvatarUrl: String(
          capsule?.businessAvatarUrl || sponsoredAvatarUrl || ""
        ).trim(),
        creatorProfileImage: senderUser?.profileImage || "",
        senderProfileImage: capsule?.senderProfileImage || "",
      }),
    [
      capsule?.capsuleType,
      capsule?.businessAvatarUrl,
      capsule?.senderProfileImage,
      senderUser?.profileImage,
      showsBusinessIdentity,
      sponsoredAvatarUrl,
    ]
  );
  const canShowDiscussion = useMemo(
    () => String(capsule?.capsuleType || "").toLowerCase() === "private",
    [capsule?.capsuleType]
  );
  const canLoveCapsule =
    Boolean(capsule?.id);
  const seededTypeKey = String(capsule?.seedType || "").trim().toLowerCase();
  const shouldShowSeedTypeIcon = capsule?.seeded === true && Boolean(SEED_TYPE_ICON_BY_KEY[seededTypeKey]);
  const SeedTypeIcon = shouldShowSeedTypeIcon ? SEED_TYPE_ICON_BY_KEY[seededTypeKey] : null;
  const isLoved = getLovedTimeCapsuleIds(viewerProfile).includes(capsule?.id);
  const capsuleTypeLabel = useMemo(
    () => getCapsuleDisplayLabel(capsule, uid || ""),
    [capsule, uid]
  );
  const senderLabel = useMemo(() => {
    const previewDisplayName = String(capsule?.senderDisplayName || "").trim();
    const previewPublicHandle = String(capsule?.senderPublicHandle || "").trim().replace(/^@+/, "");
    if (!senderUser && (previewDisplayName || previewPublicHandle)) {
      return ellipsizeProfileText(previewDisplayName || previewPublicHandle);
    }
    return computeSenderLabel(senderUser);
  }, [capsule?.senderDisplayName, capsule?.senderPublicHandle, senderUser]);
  const senderAvatarAttrs = useMemo(
    () => buildAvatarImageAttrs(senderAvatarUrl, { size: 40 }),
    [senderAvatarUrl]
  );
  const senderPublicHandle = useMemo(
    () =>
      formatPublicHandle(
        senderUser?.publicHandle ||
          senderUser?.userName ||
          capsule?.senderPublicHandle
      ),
    [capsule?.senderPublicHandle, senderUser?.publicHandle, senderUser?.userName]
  );
  const sponsoredBusinessName = useMemo(
    () =>
      String(
        sponsoredBusiness?.displayName ||
        sponsoredBusiness?.companyName ||
        ""
      ).trim(),
    [sponsoredBusiness?.companyName, sponsoredBusiness?.displayName]
  );
  const senderDisplayLabel = useMemo(() => {
    if (showsBusinessIdentity) {
      if (sponsoredBusinessName) return ellipsizeProfileText(sponsoredBusinessName);
      return senderLabel;
    }
    return senderLabel;
  }, [showsBusinessIdentity, senderLabel, sponsoredBusinessName]);
  const senderInitials = useMemo(() => getInitials(senderDisplayLabel), [senderDisplayLabel]);
  const navigationUrl = useMemo(
    () =>
      getDeviceNavigationUrl({
        address: capsule?.location?.address,
        lat: targetLatLng?.lat,
        lng: targetLatLng?.lng,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
      }),
    [capsule?.location?.address, targetLatLng?.lat, targetLatLng?.lng]
  );
  const senderProfilePath = useMemo(() => {
    if (showsBusinessIdentity) {
      const resolvedBusinessId = String(capsule?.businessId || capsule?.senderId || "").trim();
      return resolvedBusinessId ? `/business/${resolvedBusinessId}` : "";
    }
    const senderId = String(capsule?.senderId || "").trim();
    return senderId ? getProfilePathByUid(senderId) : "";
  }, [capsule?.businessId, capsule?.senderId, showsBusinessIdentity]);
  const sponsoredBusinessId = useMemo(
    () => String(capsule?.businessId || capsule?.senderId || "").trim(),
    [capsule?.businessId, capsule?.senderId]
  );
  const isSubscribedForMemberCapsules = hasInsiderAccess(subscriptionData || viewerProfile, subscriptionTier);
  const routeCapsulePreview = location.state?.capsule || {};
  const loadingCapsulePreview = capsule
    ? {
        ...routeCapsulePreview,
        ...capsule,
        isSubscribedUser: isSubscribedForMemberCapsules,
        icon: routeCapsulePreview?.icon || capsule?.icon,
        businessAvatarUrl:
          capsule?.businessAvatarUrl || routeCapsulePreview?.businessAvatarUrl,
      }
    : {
        ...routeCapsulePreview,
        isSubscribedUser: isSubscribedForMemberCapsules,
      };
  const loadingCapsuleIcon = resolveUnlockingCapsuleIcon(loadingCapsulePreview, uid);
  const loadingCapsuleTitle =
    loadPhase === "verifying"
      ? "Verifying this shared link..."
      : shareToken
        ? "Opening your time capsule..."
        : "Opening time capsule...";
  const selfRecipientEntry = useMemo(() => {
    const avatarUrl = String(
      senderAvatarUrl ||
      auth.currentUser?.photoURL ||
      ""
    ).trim();
    return {
      name: "You",
      subtitle: senderPublicHandle,
      avatarUrl,
    };
  }, [senderAvatarUrl, senderPublicHandle]);
  const isSelfCapsule = useMemo(() => {
    if (capsule?.capsuleType !== "private") return false;
    if (capsule?.isSelfCapsule === true || capsule?.audience === "self") return true;

    const senderId = capsule?.senderId || "";
    const recipientUserIds = Array.isArray(capsule?.recipientUserIds)
      ? capsule.recipientUserIds.filter(Boolean)
      : [];
    const claimedBy = Array.isArray(capsule?.claimedBy) ? capsule.claimedBy.filter(Boolean) : [];
    const recipientPhones = Array.isArray(capsule?.recipientPhoneNumbers)
      ? capsule.recipientPhoneNumbers.filter(Boolean)
      : [];

    const recipientOnlySender =
      recipientUserIds.length > 0 && recipientUserIds.every((id) => id === senderId);
    const claimedOnlySender =
      claimedBy.length > 0 && claimedBy.every((id) => id === senderId);

    return recipientPhones.length === 0 && (recipientOnlySender || claimedOnlySender);
  }, [capsule]);

  React.useEffect(() => {
    if (!uid) {
      setViewerProfile(null);
      setViewerProfileLoaded(false);
      setViewerProfileLoadFailed(false);
      return undefined;
    }

    let cancelled = false;
    setViewerProfileLoaded(false);
    setViewerProfileLoadFailed(false);

    // This read decides whether a member-exclusive capsule opens, so one transient
    // failure must not decide it — retry once, and if it still fails, record that we
    // could not verify. The gate below then declines to send the viewer to the
    // upsell rather than guessing they are not an Insider.
    const loadViewerProfile = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const profile = await fetchUserProfileFromFirestore(uid);
          if (cancelled) return;
          setViewerProfile(profile);
          setViewerProfileLoaded(true);
          return;
        } catch (err) {
          if (cancelled) return;
          if (attempt === 0) {
            await new Promise((resolve) => setTimeout(resolve, 400));
            if (cancelled) return;
            continue;
          }
          console.error("Failed to load viewer profile:", err);
          Sentry.captureMessage("[capsule-gate] could not verify viewer Insider status", {
            level: "warning",
            tags: { feature: "member-exclusive-gate" },
            extra: {
              capsuleId: id,
              errorCode: err?.code || null,
              errorMessage: err?.message || String(err || ""),
            },
          });
          setViewerProfileLoadFailed(true);
          // The gate waits on this flag, so a failed fetch has to release it too —
          // otherwise the capsule stays withheld forever.
          setViewerProfileLoaded(true);
        }
      }
    };

    loadViewerProfile();

    return () => {
      cancelled = true;
    };
  }, [uid, id]);

  React.useEffect(() => {
    let cancelled = false;

    const resolveAdminRole = async () => {
      if (!uid || !auth.currentUser) {
        if (!cancelled) setIsAdminUser(false);
        return;
      }

      try {
        const result = await auth.currentUser.getIdTokenResult(true);
        if (cancelled) return;
        const claims = result?.claims || {};
        const rolesFromToken = normalizeRoleList(claims?.roles);
        const rolesFromNested = normalizeRoleList(claims?.customClaims?.roles);
        const rolesFromBooleanAdmin = claims?.admin === true ? ["admin"] : [];
        const tokenRoles = Array.from(
          new Set([...rolesFromToken, ...rolesFromNested, ...rolesFromBooleanAdmin])
        );

        const profileRolesFromCustom = normalizeRoleList(viewerProfile?.customClaims?.roles);
        const profileRolesFromLegacy = normalizeRoleList(viewerProfile?.roles);
        const profileRolesFromFlag = viewerProfile?.isAdmin === true ? ["admin"] : [];
        const effectiveRoles = Array.from(
          new Set([
            ...tokenRoles,
            ...profileRolesFromCustom,
            ...profileRolesFromLegacy,
            ...profileRolesFromFlag,
          ])
        );
        setIsAdminUser(effectiveRoles.includes("admin"));
      } catch (err) {
        console.warn("Failed to resolve admin role for capsule actions:", err);
        if (!cancelled) setIsAdminUser(false);
      }
    };

    resolveAdminRole();

    return () => {
      cancelled = true;
    };
  }, [uid, viewerProfile]);

  React.useEffect(() => {
    setInsiderOverride(null);
  }, [capsule?.id, capsule?.insider, capsule?.adminStarred]);

  React.useEffect(() => {
    if (!loading) {
      setShowSlowLoadHint(false);
      setShowExtendedLoadHint(false);
      return undefined;
    }

    const slowTimer = setTimeout(() => setShowSlowLoadHint(true), 4000);
    const extendedTimer = setTimeout(() => setShowExtendedLoadHint(true), 10000);
    return () => {
      clearTimeout(slowTimer);
      clearTimeout(extendedTimer);
    };
  }, [loading, loadPhase]);

  React.useEffect(() => {
    let cancelled = false;
    const loadFollowingStatus = async () => {
      if (!uid || !showsBusinessIdentity || !sponsoredBusinessId) {
        if (!cancelled) setIsFollowingSponsoredBusiness(false);
        return;
      }
      try {
        const status = await isFollowingBusiness(uid, sponsoredBusinessId);
        if (!cancelled) setIsFollowingSponsoredBusiness(Boolean(status));
      } catch (err) {
        console.warn("Failed to load sponsored business follow status:", err);
        if (!cancelled) setIsFollowingSponsoredBusiness(false);
      }
    };
    loadFollowingStatus();
    return () => {
      cancelled = true;
    };
  }, [showsBusinessIdentity, sponsoredBusinessId, uid]);

  // memberExclusive capsules opened by direct link have to clear the same Insider bar the
  // map already enforces on its pins — otherwise sharing a capsule URL routes around the
  // funnel entirely.
  //
  // Admin status is resolved twice on purpose: `isAdminUser` lands asynchronously (it
  // awaits getIdTokenResult), so on its own it would let an admin be redirected before
  // their role arrives. Profile-derived roles are available the moment the gate opens.
  const viewerProfileIsAdmin = useMemo(() => {
    const roles = new Set([
      ...normalizeRoleList(viewerProfile?.customClaims?.roles),
      ...normalizeRoleList(viewerProfile?.roles),
      ...(viewerProfile?.isAdmin === true ? ["admin"] : []),
    ]);
    return roles.has("admin");
  }, [viewerProfile]);
  const isMemberExclusiveGateExempt =
    Boolean(uid && capsule?.senderId && uid === capsule.senderId) ||
    isAdminUser ||
    viewerProfileIsAdmin;
  // True for any capsule this viewer is not entitled to see. Non-memberExclusive capsules
  // are never gated, so this is false for them.
  //
  // A signed-in viewer whose profile could not be read is deliberately let through: we
  // cannot tell an Insider from a free user there, and showing a real Insider the upsell
  // is the worse failure. Signed-out viewers are unaffected — they never reach the fetch.
  const shouldWithholdMemberExclusive =
    !isMemberExclusiveGateExempt &&
    !viewerProfileLoadFailed &&
    shouldGateMemberExclusiveContent(
      capsule,
      subscriptionData || viewerProfile,
      subscriptionTier
    );
  // The redirect waits for the viewer's profile, but the content is withheld immediately.
  // On a cold deep link the persisted store is empty and the profile arrives async, so
  // deciding early would bounce a real Insider to the upsell.
  const isMemberExclusiveGateResolved = !uid || viewerProfileLoaded;

  // One log per capsule, so a re-render before the redirect lands can't re-fire it.
  // (The server dedups too, but there is no reason to make the call twice.)
  const gateBlockLoggedRef = React.useRef("");

  React.useEffect(() => {
    if (!shouldWithholdMemberExclusive || !isMemberExclusiveGateResolved) return;

    // Diagnostic only — sizes the deep-link funnel and splits it by audience.
    // This cannot see the raw-API exposure, which never loads this app.
    const blockedCapsuleId = capsule?.id || id;
    if (gateBlockLoggedRef.current !== blockedCapsuleId) {
      gateBlockLoggedRef.current = blockedCapsuleId;
      logCapsuleEvent("gate_blocked", capsule, {
        token: shareToken || undefined,
        meta: { audience: uid ? "signed_in_non_member" : "signed_out" },
      });
    }

    const returnPath = `${location.pathname}${location.search || ""}`;
    if (!uid) {
      navigate(`/signin?next=${encodeURIComponent(returnPath)}`, {
        replace: true,
        state: { from: location },
      });
      return;
    }

    navigate("/membership", {
      replace: true,
      state: {
        returnTo: returnPath,
        capsuleId: capsule?.id || id,
        source: "capsule-deeplink",
      },
    });
  }, [
    shouldWithholdMemberExclusive,
    isMemberExclusiveGateResolved,
    uid,
    location,
    navigate,
    capsule,
    shareToken,
    id,
  ]);

  // Withhold during render, not just in the effect above: effects run after paint, so an
  // effect-only gate would flash one frame of the exclusive content.
  if (shouldWithholdMemberExclusive) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.25,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={(theme) => ({
            position: "relative",
            width: 118,
            height: 118,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            background:
              theme.palette.mode === "dark"
                ? `radial-gradient(circle at 50% 42%, ${alpha(theme.palette.primary.light, 0.28)}, ${alpha(theme.palette.background.paper, 0.92)} 62%)`
                : `radial-gradient(circle at 50% 42%, ${alpha(theme.palette.primary.light, 0.18)}, ${alpha(theme.palette.background.paper, 0.98)} 66%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.24 : 0.18)}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 22px 58px ${alpha(theme.palette.common.black, 0.34)}, 0 0 42px ${alpha(theme.palette.primary.main, 0.16)}`
                : `0 22px 46px ${alpha(theme.palette.primary.main, 0.12)}, 0 10px 28px ${alpha(theme.palette.common.black, 0.08)}`,
            mb: 0.5,
          })}
        >
          <CircularProgress
            size={106}
            thickness={2.2}
            sx={(theme) => ({
              position: "absolute",
              color: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.72 : 0.62),
            })}
          />
          <Box
            component="img"
            src={loadingCapsuleIcon}
            alt=""
            sx={{
              width: 72,
              height: 72,
              objectFit: "contain",
              filter: "drop-shadow(0 12px 18px rgba(0,0,0,0.28))",
            }}
          />
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 700 }}>
          {loadingCapsuleTitle}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75, maxWidth: 360 }}>
          {loadPhase === "verifying"
            ? "We are checking that this shared link is valid."
            : "We are loading the capsule details now."}
        </Typography>
        {showSlowLoadHint && (
          <Typography variant="body2" sx={{ opacity: 0.78, maxWidth: 380 }}>
            This is taking longer than usual, but the app is still working on it.
          </Typography>
        )}
        {showExtendedLoadHint && (
          <Typography variant="body2" sx={{ opacity: 0.78, maxWidth: 380 }}>
            Still opening this capsule. Slow networks or a cold server can add a little extra time.
          </Typography>
        )}
        {sessionCapabilities.isLimitedSession && (
          <Alert severity="info" sx={{ mt: 1, maxWidth: 420, textAlign: "left" }}>
            Private browsing or browser privacy settings may slow this down or prevent access from being saved.
          </Alert>
        )}
      </Box>
    );
  }

  if (!capsule) {
    const hasEnded = isCapsuleEndedError(capsuleLoadError);
    return (
      <Box p={2} sx={{ display: "grid", gap: 1.5 }}>
        <Alert severity={hasEnded ? "info" : shareToken ? "warning" : "error"}>
          {getCapsuleLoadErrorMessage(capsuleLoadError, Boolean(shareToken))}
        </Alert>
        {!hasEnded && sessionCapabilities.isLimitedSession && (
          <Alert severity="info">
            Private browsing or browser privacy settings can interrupt shared links and sign-in persistence. Try opening this link in a regular browser tab if it keeps failing.
          </Alert>
        )}
        {!hasEnded && (
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
        )}
      </Box>
    );
  }

  const isSharedPreviewLocked = Boolean(
    shareToken &&
      !uid &&
      sharedAccessLevel === "preview" &&
      capsule?.previewLocked === true
  );

  if (isSharedPreviewLocked) {
    const senderName = senderDisplayLabel || "Someone";
    const inventory = capsule?.previewInventory || {};
    const inventorySummary = buildPreviewInventorySummary(inventory);
    const previewMediaTiles = buildPreviewMediaTiles(capsule);
    const hasPlacePreview = Boolean(inventory.hasPlace);
    const hasNotePreview = Boolean(inventory.hasNote);
    return (
      <Container
        maxWidth="sm"
        sx={{
          minHeight: "100%",
          display: "block",
          px: 2,
          pt: 2,
          pb: `calc(env(safe-area-inset-bottom, 0px) + 28px)`,
        }}
      >
        <Box sx={{ mt: 1, mb: 2.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Avatar
            src={senderAvatarAttrs.src || undefined}
            imgProps={{
              srcSet: senderAvatarAttrs.srcSet || undefined,
              sizes: senderAvatarAttrs.sizes || undefined,
              loading: "lazy",
              decoding: "async",
            }}
            sx={(theme) => ({
              width: 42,
              height: 42,
              border: `2px solid ${alpha(theme.palette.common.white, 0.72)}`,
              flex: "0 0 auto",
            })}
          >
            {senderAvatarUrl ? null : senderInitials}
          </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 850,
                  lineHeight: 1.18,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {senderName} made this for you
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.35 }}>
                {inventorySummary}
              </Typography>
            </Box>
          </Box>
        </Box>

        {previewMediaTiles.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: previewMediaTiles.length === 1 ? "1fr" : "repeat(2, minmax(0, 1fr))",
              gap: 1.25,
              mb: 1.25,
            }}
          >
            {previewMediaTiles.map((mediaItem, index) => {
              const label = getPreviewMediaLabel(mediaItem?.type);
              const previewUrl = getPreviewMediaUrl(mediaItem);
              const previewBackground = toCssUrl(previewUrl);
              return (
                <Box
                  key={`${label}-${index}`}
                  data-testid={`locked-preview-media-tile-${index}`}
                  style={
                    previewBackground
                      ? { "--locked-preview-bg": previewBackground }
                      : undefined
                  }
                  sx={(theme) => ({
                    position: "relative",
                    overflow: "hidden",
                    minHeight: previewMediaTiles.length === 1 ? 180 : 142,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.16)}`,
                    backgroundColor: alpha(theme.palette.text.primary, 0.08),
                    pointerEvents: "none",
                    userSelect: "none",
                  })}
                >
                  <Box
                    aria-hidden="true"
                    sx={(theme) => ({
                      position: "absolute",
                      inset: -18,
                      background: previewUrl
                        ? `
                          linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.08)}, ${alpha(theme.palette.common.black, 0.34)}),
                          var(--locked-preview-bg)
                        `
                        : getPreviewMediaBackground(mediaItem?.type, theme),
                      backgroundPosition: "center",
                      backgroundSize: "cover",
                      filter: "blur(15px)",
                      transform: "scale(1.14)",
                    })}
                  />
                  <Box
                    aria-hidden="true"
                    sx={(theme) => ({
                      position: "absolute",
                      inset: 0,
                      background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.04)}, ${alpha(theme.palette.common.black, 0.24)})`,
                    })}
                  />
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      left: 10,
                      right: 10,
                      bottom: 10,
                      minHeight: 31,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 999,
                      backgroundColor: alpha(theme.palette.background.default, 0.76),
                      color: theme.palette.common.white,
                      fontSize: "0.75rem",
                      fontWeight: 850,
                      backdropFilter: "blur(8px)",
                    })}
                  >
                    {label}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {hasPlacePreview && (
          <Box
            sx={(theme) => ({
              p: 1.5,
              mb: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
              backgroundColor: alpha(theme.palette.common.white, 0.052),
            })}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                A place they chose
              </Typography>
              <Chip size="small" label="Locked" variant="outlined" sx={{ height: 24, fontWeight: 800 }} />
            </Box>
            <Box
              sx={(theme) => ({
                mt: 1.2,
                height: 16,
                width: "84%",
                borderRadius: 999,
                backgroundColor: alpha(theme.palette.text.primary, 0.36),
                filter: "blur(3px)",
                opacity: 0.62,
              })}
            />
          </Box>
        )}

        {hasNotePreview && (
          <Box
            sx={(theme) => ({
              p: 1.5,
              mb: 1.25,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
              backgroundColor: alpha(theme.palette.common.white, 0.052),
            })}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                A note for you
              </Typography>
              <Chip size="small" label="Locked" variant="outlined" sx={{ height: 24, fontWeight: 800 }} />
            </Box>
            {[96, 68].map((width) => (
              <Box
                key={width}
                sx={(theme) => ({
                  mt: 1.1,
                  height: 13,
                  width: `${width}%`,
                  borderRadius: 999,
                  backgroundColor: alpha(theme.palette.text.primary, 0.36),
                  filter: "blur(3px)",
                  opacity: 0.62,
                })}
              />
            ))}
          </Box>
        )}

        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1.5, mx: "auto", maxWidth: 390, lineHeight: 1.55 }}
          >
            Sign in or create an account to reveal it, save it, and keep this memory connected.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => redirectToSignIn()}
            sx={(theme) => ({
              mt: 2,
              minHeight: 50,
              fontWeight: 850,
              textTransform: "none",
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              "&:hover": {
                backgroundColor: alpha(theme.colors.blue, 0.9),
              },
            })}
          >
            Sign in to reveal
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.25 }}>
            We will bring you right back here.
          </Typography>
        </Box>
      </Container>
    );
  }

  const revealDate = parseRevealDate(capsule?.revealDate);
  const isLocked = Boolean(revealDate && revealDate > new Date());
  const isSender = Boolean(uid && uid === capsule?.senderId);
  const isPrivateShareLinkCapsule = Boolean(
    isPrivateCapsule && capsule?.deliveryMode === "share_claim"
  );
  const isLockedPrivateRecipient = Boolean(isPrivateCapsule && !isSender && isLocked);
  const canViewRecipientSummary = Boolean(
    flags.isPrivate &&
      uid &&
      (isSender ||
        (Array.isArray(capsule?.recipientUserIds) && capsule.recipientUserIds.includes(uid)) ||
        (Array.isArray(capsule?.claimedBy) && capsule.claimedBy.includes(uid)))
  );

  // sentAt may arrive as a Firestore Timestamp, an ISO string, or a serialized
  // shape depending on the read path (e.g. a freshly-claimed shared envelope),
  // so normalize it rather than assuming `.seconds`.
  const sentDate = parseRevealDate(capsule.sentAt);
  const sentAtString = sentDate ? sentDate.toLocaleString() : "Unknown date";

  const readBySet = new Set(
    Array.isArray(capsule?.readBy) ? capsule.readBy.filter(Boolean) : []
  );

  const buildRecipientEntryFromUser = (record, fallbackUid = "") => {
    const displayName = typeof record?.displayName === "string" ? record.displayName.trim() : "";
    const publicHandle = formatPublicHandle(record?.publicHandle || record?.userName);
    const recipientUid = String(record?.uid || fallbackUid || "").trim();
    const isRead = Boolean(recipientUid && readBySet.has(recipientUid));
    return {
      name: ellipsizeProfileText(displayName || publicHandle || "Connection"),
      subtitle: displayName && publicHandle ? ellipsizeProfileText(publicHandle) : "",
      avatarUrl: record?.profileImage || "",
      statusLabel: isRead ? "Read" : "Unread",
      statusColor: isRead ? "success" : "default",
    };
  };

  const recipientEntries = (() => {
    const byUid = Array.isArray(capsule?.recipientUserIds)
      ? capsule.recipientUserIds.filter(Boolean)
      : [];
    return byUid.map((recipientUid) => {
      const record = usersList.find((u) => u.uid === recipientUid);
      return buildRecipientEntryFromUser(record, recipientUid);
    });
  })();

  const claimEntries = (capsule?.claimedBy || [])
    .filter((claimUid) => claimUid && claimUid !== capsule?.senderId)
    .map((claimUid) => {
      const record = usersList.find((u) => u.uid === claimUid);
      return buildRecipientEntryFromUser(record, claimUid);
    });

  const countdownRenderer = ({ total, completed }) => {
    if (completed) return <Typography gutterBottom>This time capsule is now open!</Typography>;
    const duration = formatDuration(total);
    return (
      <Typography gutterBottom>
        {messageToBeOppendText} {duration}
      </Typography>
    );
  };

  const openLightbox = (_media, index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const rememberHiddenCapsule = (capsuleId) => {
    if (!capsuleId) return;
    try {
      const existing = JSON.parse(sessionStorage.getItem(HIDDEN_MAP_CAPSULE_IDS_KEY) || "[]");
      const asList = Array.isArray(existing) ? existing : [];
      const next = Array.from(new Set([...asList, capsuleId]));
      sessionStorage.setItem(HIDDEN_MAP_CAPSULE_IDS_KEY, JSON.stringify(next));
    } catch {
      // Ignore storage failures; DB state still updates.
    }
  };

  const closeToMapAtCurrentCapsule = (capsuleId) => {
    const lat = Number(capsule?.location?.lat);
    const lng = Number(capsule?.location?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      navigate(-1);
      return;
    }

    navigate("/", {
      state: {
        center: { lat, lng },
        hiddenCapsuleId: capsuleId,
        hiddenCapsuleAt: Date.now(),
      },
    });
  };

  const archiveMessage = async (capsuleId) => {
    if (!uid) return;
    const ref = doc(db, "timeCapsules", capsuleId);
    await updateDoc(ref, { archivedBy: arrayUnion(uid) });
    rememberHiddenCapsule(capsuleId);
    setSnackbar({
      open: true,
      message: "Message archived successfully!",
      type: "info",
    });
    closeToMapAtCurrentCapsule(capsuleId);
  };

  const deleteMessage = async (capsuleId) => {
    if (!uid) return;
    const ref = doc(db, "timeCapsules", capsuleId);
    await updateDoc(ref, { deletedBy: arrayUnion(uid) });
    rememberHiddenCapsule(capsuleId);
    setSnackbar({
      open: true,
      message: "Message deleted successfully!",
      type: "error",
    });
    closeToMapAtCurrentCapsule(capsuleId);
  };

  const handleViewOnMap = () => {
    if (!capsule.location) return;
    navigate("/", {
      state: {
        center: {
          lat: capsule.location.lat,
          lng: capsule.location.lng,
        },
        sentCapsuleId: capsule.id,
        focusedCapsuleId: capsule.id,
        suppressClusterRestore: true,
      },
    });
  };

  const handleForward = () => {
    if (!capsule?.location) return;
    const forwardPath = `/timecapsule?lat=${capsule.location.lat}&lng=${capsule.location.lng}&address=${encodeURIComponent(
      capsule.location.address || ""
    )}`;
    if (!uid) {
      redirectToSignIn(forwardPath);
      return;
    }

    if (isMeasurableCapsule(capsule)) {
      logCapsuleEvent("share", capsule, { token: shareToken || undefined, shareId: newShareId() });
    }
    navigate(
      forwardPath,
      {
        state: {
          capsule: {
            ...buildForwardWizardCapsule(capsule),
            ...(shareToken ? { sourceShareToken: shareToken } : {}),
          },
          forwardMode: "editable",
          forceFreshSession: true,
        },
      }
    );
  };

  const handleWalkHere = () => {
    if (!navigationUrl) return;
    window.location.href = navigationUrl;
  };

  const handleViewInCamera = () => {
    if (!targetLatLng || !capsule?.id) return;
    const title = capsule.title || capsule.messageTitle || "Time Capsule";
    navigate(`/ar/capsule/${capsule.id}`, {
      state: {
        capsuleId: capsule.id,
        capsuleType: capsule.capsuleType || "default",
        targetLat: targetLatLng.lat,
        targetLng: targetLatLng.lng,
        iconUrl: senderAvatarUrl,
        title,
      },
    });
  };

  // Featured CTA click → record a cta_click measurement event (server resolves
  // identity + dedup), then open the destination. Fire-and-forget in the same
  // user gesture so the popup isn't blocked. If the CTA is an internal raffle/
  // coupon claim, stay in-app and carry the capsule attribution so the eventual
  // server-authoritative `claim` credits this capsule (standard §3.5 step 4b).
  const handleCtaClick = () => {
    if (!callToActionUrl) return;
    logCapsuleEvent("cta_click", capsule, { token: shareToken || undefined });
    const claim = resolveClaimPathOffer(callToActionUrl);
    if (claim?.publicClaimPath) {
      navigate(claim.publicClaimPath, {
        state: {
          claimSource: "capsule_cta",
          referralCampaignId: String(capsule?.id || id || "").trim(),
        },
      });
      return;
    }
    window.open(callToActionUrl, "_blank", "noopener,noreferrer");
  };

  const handleShareCapsuleLink = async () => {
    if (!uid) {
      redirectToSignIn();
      return;
    }

    if (isMeasurableCapsule(capsule)) {
      logCapsuleEvent("share", capsule, { token: shareToken || undefined, shareId: newShareId() });
    }

    const shareUrl = `${window.location.origin}/capsule/${capsule?.id}`;
    const shareText = buildCapsuleShareMessage({
      senderName: viewerDisplayName,
      variant: getShareCopyVariant(capsule),
      shareUrl,
    });

    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
        return;
      } catch {
        // user cancelled; fallback to copy
      }
    }

    try {
      await navigator.clipboard.writeText(shareText);
      setSnackbar({ open: true, message: "Share message copied.", type: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to copy share message.", type: "error" });
    }
  };

  const canResharePending =
    isSender &&
    isPrivateShareLinkCapsule &&
    !isSelfCapsule;
  const shouldReshareFromRecipientSummary =
    canResharePending && claimEntries.length === 0;

  const handleReshare = async () => {
    const shareUrl = String(capsule?.shareUrl || "").trim();
    if (!shareUrl) {
      setSnackbar({
        open: true,
        message: "No share link available for this capsule yet.",
        type: "warning",
      });
      return;
    }

    if (navigator.share) {
      try {
        // Embed the URL in `text` — many native share targets drop a separate
        // `url` field and keep only the link, stripping the personalized copy.
        await navigator.share({
          title: capsule?.messageTitle || "A time capsule for you",
          text: buildCapsuleShareMessage({
            senderName: viewerDisplayName,
            variant: getShareCopyVariant(capsule),
            shareUrl,
          }),
        });
        return;
      } catch {
        // user cancelled; fall through to copy
      }
    }

    try {
      // Clipboard fallback carries the personalized message too (FR-E2).
      await navigator.clipboard.writeText(
        buildCapsuleShareMessage({
          senderName: viewerDisplayName,
          variant: getShareCopyVariant(capsule),
          shareUrl,
        })
      );
      setSnackbar({ open: true, message: "Share message copied.", type: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to copy share message.", type: "error" });
    }
  };

  const handleShareFromDetails = async () => {
    if (isPrivateCapsule) {
      if (isSender && isPrivateShareLinkCapsule) {
        await handleReshare();
        return;
      }
      if (isLockedPrivateRecipient) {
        setShareBlockedDialogOpen(true);
        return;
      }
      handleForward();
      return;
    }

    if (isEditableForwardCapsule) {
      handleForward();
      return;
    }

    await handleShareCapsuleLink();
  };

  const recipientSummarySecondaryActionLabel =
    isSender && canResharePending
      ? "Reshare"
      : undefined;
  const handleRecipientSummarySecondaryAction =
    isSender && canResharePending
      ? handleReshare
      : undefined;

  const canShareFromDetails = Boolean(
    (!isPrivateCapsule && (isEditableForwardCapsule || forwardShareMode === "native")) ||
    (isSender && isPrivateShareLinkCapsule) ||
    (!isSender && !isSelfCapsule)
  );

  const requestDelete = () => {
    closeMore();
    setConfirmAction({ type: "delete" });
  };

  const requestArchive = () => {
    closeMore();
    setConfirmAction({ type: "archive" });
  };

  const handleToggleInsider = async () => {
    if (!canToggleInsider || !capsule?.id || promoteBusy) return;
    const nextInsider = !isAlreadyInsiderPromoted;
    setInsiderOverride(nextInsider);
    setPromoteBusy(true);
    try {
      const ref = doc(db, "timeCapsules", capsule.id);
      await updateDoc(ref, {
        adminStarred: nextInsider,
        insider: nextInsider,
      });
      setSnackbar({
        open: true,
        message: nextInsider ? "Added to gems." : "Removed from gems.",
        type: "success",
      });
    } catch (err) {
      console.error("Failed to toggle capsule insider status:", err);
      setInsiderOverride(null);
      setSnackbar({
        open: true,
        message: "Could not update gem status.",
        type: "error",
      });
    } finally {
      setPromoteBusy(false);
    }
  };

  const handleConfirmAction = async () => {
    const actionType = confirmAction?.type;
    setConfirmAction(null);
    if (actionType === "archive") {
      await archiveMessage(capsule.id);
      return;
    }
    if (actionType === "delete") {
      await deleteMessage(capsule.id);
    }
  };

  const handleToggleLove = async () => {
    if (!uid) {
      redirectToSignIn();
      return;
    }
    if (!capsule?.id || !canLoveCapsule) return;

    try {
      await setLovedTimeCapsule(uid, capsule.id, !isLoved);
      // reaction (love) — only on the love transition, not on un-love. Server
      // dedups per campaign·identity·type so a re-love won't double count.
      if (!isLoved && isMeasurableCapsule(capsule)) {
        logCapsuleEvent("reaction", capsule, {
          token: shareToken || undefined,
          meta: { reactionType: "love" },
        });
      }
      // Update the global profile store (the source /history observes) directly and
      // authoritatively from its live value, not from the one-time-fetched local profile.
      const storeProfile = useUserProfileStore.getState().userProfile || {};
      const currentLoved = getLovedTimeCapsuleIds(storeProfile);
      const nextLoved = isLoved
        ? currentLoved.filter((id) => id !== capsule.id)
        : Array.from(new Set([...currentLoved, capsule.id]));
      setStoredUserProfile({ ...storeProfile, lovedCapsuleIds: nextLoved });
      // Keep the local view in sync so the heart icon on this page flips immediately.
      setViewerProfile((prev) => ({ ...(prev || {}), lovedCapsuleIds: nextLoved }));
    } catch (err) {
      console.error("Failed to toggle love:", err);
      setSnackbar({
        open: true,
        message: "Unable to update loved time capsules.",
        type: "error",
      });
    }
  };

  const handleToggleFollowSponsoredBusiness = async () => {
    if (!uid) {
      redirectToSignIn();
      return;
    }
    if (!showsBusinessIdentity || !sponsoredBusinessId || followBusy) return;
    setFollowBusy(true);
    try {
      if (isFollowingSponsoredBusiness) {
        await unfollowBusiness(uid, sponsoredBusinessId);
        setIsFollowingSponsoredBusiness(false);
        // unfollow — attributed to this sponsored capsule's campaign.
        logCapsuleFollow(capsule, false, { token: shareToken || undefined });
        setSnackbar({
          open: true,
          message: "Unfollowed business.",
          type: "info",
        });
      } else {
        await followBusiness(uid, {
          id: sponsoredBusinessId,
          displayName: sponsoredBusinessName || senderDisplayLabel,
          companyName: sponsoredBusiness?.companyName || "",
          avatarUrl: sponsoredAvatarUrl || capsule?.businessAvatarUrl || "",
        });
        setIsFollowingSponsoredBusiness(true);
        // follow — attributed to this sponsored capsule's campaign.
        logCapsuleFollow(capsule, true, { token: shareToken || undefined });
        setSnackbar({
          open: true,
          message: "Now following business.",
          type: "success",
        });
      }
    } catch (err) {
      console.error("Failed to toggle follow for sponsored business:", err);
      setSnackbar({
        open: true,
        message: "Could not update follow status.",
        type: "error",
      });
    } finally {
      setFollowBusy(false);
    }
  };

  return (
    <>
      <Container
        maxWidth="sm"
        sx={{
          pt: {
            xs: 2,
            sm: 2,
            md: 2,
          },
          pb: `calc(env(safe-area-inset-bottom, 0px) + ${contentBottomPadding}px)`,
        }}
      >
        {/* Media */}
        {claimStatus === "failed" && shareToken && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You can view this capsule now, but we could not save it to your account. Sign in from a regular browser tab if you want access to persist.
          </Alert>
        )}
        {claimError && sessionCapabilities.isLimitedSession && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Private browsing or browser privacy settings may prevent this shared access from being saved.
          </Alert>
        )}
        <Box>
          <MediaSlider
            items={capsule.media}
            isLocked={isLocked}
            onOpenLightbox={openLightbox}
            onMediaError={(mediaItem) => {
              repairSeededPlaceMedia(mediaItem);
            }}
          />
        </Box>

        {/* Recipients + Sent-at */}
        {canViewRecipientSummary && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              width: "100%",
            }}
          >
            <SentToDisplay
              recipients={isSelfCapsule
                ? [selfRecipientEntry]
                : (capsule.deliveryMode === "share_claim" ? claimEntries : recipientEntries)}
              triggerLabel={isSelfCapsule
                ? "Your memory"
                : (isSender && capsule.deliveryMode === "share_claim" && claimEntries.length === 0
                    ? "Shared link not claimed yet, reshare"
                    : undefined)}
              dialogTitleOverride={isSelfCapsule ? "Recipient" : undefined}
              summaryMode={capsule.deliveryMode === "share_claim" ? "claimed" : "sent"}
              onTriggerClick={isSender && shouldReshareFromRecipientSummary ? handleReshare : undefined}
              dialogSecondaryActionLabel={recipientSummarySecondaryActionLabel}
              onDialogSecondaryAction={handleRecipientSummarySecondaryAction}
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              {sentAtString}
            </Typography>
          </Box>
        )}

        {/* Avatar + Name */}
        {(isPrivateCapsule || isFeaturedCapsule) && (
          <Box sx={{ mt: isFeaturedCapsule ? 1.75 : 0, mb: isFeaturedCapsule ? 2.25 : 2 }}>
            {loadingSender ? (
              <CircularProgress size={24} />
            ) : (senderUser || (showsBusinessIdentity && sponsoredBusinessName)) ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, minWidth: 0 }}>
                  <Box
                    component={senderProfilePath ? RouterLink : "div"}
                    to={senderProfilePath || undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      flex: "1 1 auto",
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Avatar
                      src={senderAvatarAttrs.src || undefined}
                      imgProps={{
                        srcSet: senderAvatarAttrs.srcSet || undefined,
                        sizes: senderAvatarAttrs.sizes || undefined,
                        loading: "lazy",
                        decoding: "async",
                      }}
                    >
                      {senderAvatarUrl ? null : senderInitials}
                    </Avatar>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {senderDisplayLabel}
                    </Typography>
                  </Box>
                  {showsBusinessIdentity && isFollowingSponsoredBusiness ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CheckRoundedIcon />}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToggleFollowSponsoredBusiness();
                      }}
                      disabled={followBusy}
                      sx={(theme) => ({
                        flex: "0 0 auto",
                        minWidth: 112,
                        px: 1.35,
                        py: 0.7,
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 800,
                        lineHeight: 1.2,
                        borderColor: alpha(theme.palette.success.main, 0.58),
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.success.light
                            : theme.palette.success.dark,
                        backgroundColor: alpha(theme.palette.success.main, 0.08),
                        "&:hover": {
                          borderColor: alpha(theme.palette.success.main, 0.76),
                          backgroundColor: alpha(theme.palette.success.main, 0.12),
                        },
                        "& .MuiButton-startIcon": {
                          mr: 0.35,
                          ml: -0.25,
                        },
                      })}
                    >
                      Following
                    </Button>
                  ) : null}
                </Box>

                {showsBusinessIdentity && !isFollowingSponsoredBusiness ? (
                  <Box
                    sx={(theme) => ({
                      mt: 1.85,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.25,
                      py: 1.35,
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.32 : 0.24)}`,
                      backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === "dark" ? 0.1 : 0.07),
                    })}
                  >
                    <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                        Stay in the loop
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25, lineHeight: 1.25 }}
                      >
                        New drops from this business appear in Following.
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={handleToggleFollowSponsoredBusiness}
                      disabled={followBusy}
                      sx={(theme) => ({
                        flex: "0 0 auto",
                        minWidth: 92,
                        px: 1.6,
                        py: 0.9,
                        fontWeight: 800,
                        color: theme.palette.getContrastText(theme.palette.success.main),
                        backgroundColor: theme.palette.success.main,
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.success.main, 0.9),
                        },
                        "& .MuiButton-startIcon": {
                          mr: 0.35,
                          ml: -0.25,
                        },
                      })}
                    >
                      Follow
                    </Button>
                  </Box>
                ) : null}
              </>
            ) : null}
          </Box>
        )}

        <Divider />

        <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Chip
            label={capsuleTypeLabel}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          {flags.isPrivate && (
            <>
              <IconButton
                onClick={openMore}
                aria-label="More actions"
                sx={{ flex: "0 0 auto", width: 34, height: 34 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              <Menu
                anchorEl={moreAnchorEl}
                open={moreOpen}
                onClose={closeMore}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <MenuItem onClick={requestArchive}>
                  <ListItemIcon>
                    <ArchiveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Archive" />
                </MenuItem>

                <MenuItem onClick={requestDelete}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Delete" />
                </MenuItem>
              </Menu>
            </>
          )}
          {canToggleInsider && (
            <IconButton
              onClick={handleToggleInsider}
              aria-label={isAlreadyInsiderPromoted ? "Remove from gems" : "Add to gems"}
              disabled={promoteBusy}
              sx={(theme) => ({
                flex: "0 0 auto",
                width: 34,
                height: 34,
                border: "1px solid",
                borderColor: isAlreadyInsiderPromoted
                  ? theme.palette.warning.main
                  : theme.palette.divider,
                color: isAlreadyInsiderPromoted
                  ? theme.palette.warning.main
                  : theme.palette.text.secondary,
              })}
            >
              {isAlreadyInsiderPromoted ? (
                <StarIcon fontSize="small" />
              ) : (
                <StarBorderIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>

        {/* Body or Countdown */}
        {isLocked ? (
          <Box sx={{ textAlign: "center", my: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <img
                src={IconTimeCapsuleLockedMarker}
                alt="Locked time capsule"
                style={{ width: 120, height: 120 }}
              />
            </Box>
            <Countdown date={revealDate} renderer={countdownRenderer} />
          </Box>
        ) : (
          <Box sx={{ my: 2 }}>
            {/* Address */}
            {capsule.location?.address && (
              <Typography
                variant="caption"
                color="textSecondary"
                align="left"
                sx={{ display: "block", mb: 0 }}
              >
                {capsule.location.address}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              {SeedTypeIcon ? (
                <Box
                  sx={(theme) => ({
                    width: 30,
                    height: 30,
                    borderRadius: "999px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.colors.green, 0.2)
                        : alpha(theme.colors.green, 0.12),
                    color: theme.colors.green,
                    flexShrink: 0,
                  })}
                >
                  <SeedTypeIcon sx={{ fontSize: 18 }} />
                </Box>
              ) : null}
              <Typography variant="h6" sx={{ m: 0 }}>
                {capsule.messageTitle}
              </Typography>
            </Box>

              {hasPlaceVisitInfo ? (
                <Box
                  sx={(theme) => ({
                    my: 1.5,
                    p: 1.25,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                    backgroundColor: alpha(theme.palette.text.primary, 0.04),
                  })}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Visit Info
                    </Typography>
                    {placeVisitInfo.actions.length ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                        {placeVisitInfo.actions.map((action) => {
                          const isWebsite = action.type === "website";
                          const ActionIcon = isWebsite ? LanguageIcon : PhoneIcon;
                          return (
                            <Button
                              key={action.type}
                              href={action.href}
                              target={isWebsite ? "_blank" : undefined}
                              rel={isWebsite ? "noopener noreferrer" : undefined}
                              variant="text"
                              size="small"
                              startIcon={<ActionIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                minWidth: 0,
                                textTransform: "none",
                                fontWeight: 800,
                                whiteSpace: "nowrap",
                                "& .MuiButton-startIcon": { mr: 0.35 },
                              }}
                            >
                              {action.label}
                            </Button>
                          );
                        })}
                      </Box>
                    ) : null}
                  </Box>
                  {placeVisitInfo.facts.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {placeVisitInfo.facts.map((item) => (
                        <Chip
                          key={item.label}
                          size="small"
                          label={
                            item.label && item.label !== "Category"
                              ? `${item.label} ${item.value}`
                              : item.value
                          }
                          sx={(theme) => ({
                            height: 28,
                            borderRadius: "999px",
                            fontWeight: 700,
                            backgroundColor: alpha(theme.colors.blue, 0.14),
                            border: `1px solid ${alpha(theme.colors.blue, 0.24)}`,
                            "& .MuiChip-label": { px: 1 },
                          })}
                        />
                      ))}
                    </Box>
                  ) : null}
                  {placeVisitInfo.openStatus ? (
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        display: "block",
                        mt: placeVisitInfo.facts.length ? 1 : 0,
                        color:
                          placeVisitInfo.openStatus.tone === "open"
                            ? theme.colors.green
                            : theme.palette.text.secondary,
                        fontWeight: 800,
                      })}
                    >
                      {placeVisitInfo.openStatus.text}
                    </Typography>
                  ) : null}
                  {placeVisitInfo.hours ? (
                    <Box
                      component="details"
                      sx={(theme) => ({
                        mt: placeVisitInfo.facts.length || placeVisitInfo.openStatus ? 1 : 0,
                        "& summary": {
                          cursor: "pointer",
                          color: theme.palette.text.secondary,
                          fontSize: "0.82rem",
                          fontWeight: 800,
                          listStyle: "none",
                        },
                        "& summary::-webkit-details-marker": { display: "none" },
                      })}
                    >
                      <Box component="summary">{placeVisitInfo.hours.label}</Box>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.75, whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                      >
                        {placeVisitInfo.hours.value}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              ) : null}

              {quickTake.quickTakeText ? (
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                    {quickTake.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: "1.8rem",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {quickTake.quickTakeText}
                  </Typography>
                  {quickTake.bullets.length ? (
                    <Box component="ul" sx={{ mt: 1.25, mb: 0, pl: 2.3 }}>
                      {quickTake.bullets.map((item) => (
                        <Typography
                          component="li"
                          variant="body2"
                          key={item.label}
                          sx={{ mb: 0.45, lineHeight: 1.45 }}
                        >
                          <Box component="span" sx={{ fontWeight: 800 }}>
                            {item.label}:
                          </Box>{" "}
                          {item.value}
                        </Typography>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              ) : null}

              {quickTake.story ? (
                <Box sx={{ my: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                    {isForwardedFeaturedEnvelope
                      ? (senderDisplayLabel
                          ? `A note from ${senderDisplayLabel}`
                          : "A note for you")
                      : "Inssa's take"}
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{
                      lineHeight: "1.8rem",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {quickTake.story}
                  </Typography>
                </Box>
              ) : null}
          </Box>
        )}

        {/* Forwarded featured source — the original sponsored/memberExclusive
            content this envelope references, resolved live (FR-A2). */}
        {isForwardedFeaturedEnvelope && !isLocked && (
          <ForwardedFeaturedSection
            status={forwardedFeatured.status}
            sourceCapsule={forwardedFeatured.sourceCapsule}
            isGated={isForwardedFeaturedGated}
            onUpgrade={() => {
              const returnTo = `/capsule/${id}`;
              navigate(`/membership?returnTo=${encodeURIComponent(returnTo)}`, {
                state: { returnTo, capsuleId: id },
              });
            }}
            onOpenLightbox={openLightbox}
            uid={uid}
            onRequireSignIn={redirectToSignIn}
            onNotify={setSnackbar}
          />
        )}

        {canShowDiscussion && (
          <>
            <CapsuleThread
              capsuleId={capsule.id}
              capsule={capsule}
              currentUserId={uid}
              usersList={usersList}
            />

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Optional CTA */}
        {shouldShowInlineCta && (
          <Button
            variant="contained"
            fullWidth
            sx={(theme) => ({
              mb: 2,
              backgroundColor: theme.palette.success.main,
              color: theme.palette.getContrastText(theme.palette.success.main),
              padding: "14px 30px",
              fontWeight: 700,
              fontSize: "1rem",
              "&:hover": {
                backgroundColor: alpha(theme.palette.success.main, 0.9),
              },
            })}
            onClick={handleCtaClick}
          >
            {callToActionText}
          </Button>
        )}

        {/* Hashtags */}
        {Array.isArray(capsule.hashtags) && capsule.hashtags.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 2,
            }}
          >
            {capsule.hashtags.map((tag) => (
              <Chip
                key={tag}
                label={`#${tag}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            ))}
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleClose}
            severity={snackbar.type}
            variant={snackbar.type === "success" ? "filled" : "standard"}
            sx={(theme) => ({
              width: "100%",
              ...(snackbar.type === "success"
                ? {
                    backgroundColor:
                      theme.palette.mode === "dark" ? theme.colors.green : undefined,
                    color: "#fff",
                    "& .MuiAlert-icon": { color: "#fff" },
                    "& .MuiAlert-action": { color: "#fff" },
                  }
                : null),
            })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <LightboxModal
          items={capsule.media || []}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onClose={closeLightbox}
        />

        <Dialog
          open={Boolean(confirmAction)}
          onClose={() => setConfirmAction(null)}
        >
          <DialogTitle>
            {confirmAction?.type === "archive" ? "Archive message?" : "Delete message?"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmAction?.type === "archive"
                ? "This will move the message to Archived."
                : "This will remove the message for you, but keep it for other recipients."}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color={confirmAction?.type === "archive" ? "warning" : "error"}
              onClick={handleConfirmAction}
            >
              {confirmAction?.type === "archive" ? "Archive" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={shareBlockedDialogOpen}
          onClose={() => setShareBlockedDialogOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Sharing unlocks after reveal</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`This private time capsule is locked until ${
                revealDate ? revealDate.toLocaleString() : "its reveal date"
              }. You can share it once it opens.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShareBlockedDialogOpen(false)} autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <VideoAdModal
        capsuleType={capsule?.capsuleType}
        capsuleId={capsule?.id || id}
        instanceKey={`capsule-detail:${location.key || capsule?.id || id}`}
        anchoredAboveFooter
        anchorBottom={adAnchorBottom}
      />

      {shouldShowPersistentCta && (
        <Box
          data-testid="persistent-capsule-cta-bar"
          sx={(theme) => ({
            position: "fixed",
            left: 0,
            right: 0,
            bottom: `calc(${actionBarBottom} + ${actionBarHeight + persistentCtaGap}px)`,
            zIndex: theme.zIndex.appBar + 1,
            px: 2,
            py: 1,
            backgroundColor: alpha(theme.palette.background.paper, 0.96),
            borderTop: "1px solid",
            borderTopColor: "divider",
            backdropFilter: "blur(12px)",
          })}
        >
          <Container maxWidth="sm" disableGutters>
            <Button
              variant="contained"
              fullWidth
              sx={(theme) => ({
                minHeight: 48,
                px: 3,
                py: 1.25,
                backgroundColor: theme.palette.success.main,
                color: theme.palette.getContrastText(theme.palette.success.main),
                fontWeight: 800,
                fontSize: "1rem",
                lineHeight: 1.2,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.success.main, 0.9),
                },
              })}
              onClick={handleCtaClick}
            >
              {callToActionText}
            </Button>
          </Container>
        </Box>
      )}

      {/* Flat sticky action bar (no elevation) */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: actionBarBottom,
          zIndex: (theme) => theme.zIndex.appBar,
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderTopColor: "divider",
          px: 2,
          py: 1.5,
        }}
      >
        <Container maxWidth="sm" disableGutters>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", width: "100%" }}>
            <IconButton
              onClick={handleWalkHere}
              aria-label="Open directions"
              disabled={!navigationUrl}
              sx={(theme) => ({
                flex: "0 0 auto",
                width: 48,
                height: 48,
                border: "1px solid",
                borderColor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.secondary, 0.45)
                    : theme.palette.divider,
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.colors.blue,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.action.hover
                    : "transparent",
                "&:hover": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.secondary, 0.65)
                      : theme.palette.text.secondary,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                },
              })}
            >
              <DirectionsIcon fontSize="small" />
            </IconButton>

            {arSupported && (
              <IconButton
                onClick={handleViewInCamera}
                aria-label="Open AR camera"
                disabled={!targetLatLng}
                sx={(theme) => ({
                  flex: "0 0 auto",
                  width: 48,
                  height: 48,
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.secondary, 0.45)
                      : theme.palette.divider,
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.colors.blue,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.hover
                      : "transparent",
                  "&:hover": {
                    borderColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.text.secondary, 0.65)
                        : theme.palette.text.secondary,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                  },
                })}
              >
                <ViewInArIcon fontSize="small" />
              </IconButton>
            )}

            <Button
              variant="contained"
              onClick={handleViewOnMap}
              sx={(theme) => ({
                backgroundColor: theme.colors.blue,
                color: theme.colors.white,
                fontWeight: 700,
                px: 1.75,
                height: 48,
                minHeight: 48,
                flex: 1,
                minWidth: 0,
                "&.Mui-disabled": {
                  opacity: 0.85,
                  color: theme.colors.white,
                },
              })}
            >
              Map
            </Button>

            {canLoveCapsule && (
              <IconButton
                onClick={handleToggleLove}
                aria-label={isLoved ? "Unlove time capsule" : "Love time capsule"}
                sx={(theme) => ({
                  flex: "0 0 auto",
                  width: 48,
                  height: 48,
                  p: 0,
                  borderRadius: "50%",
                  border: "1px solid",
                  color: isLoved ? theme.colors.white : theme.palette.text.primary,
                  borderColor: isLoved
                    ? "transparent"
                    : theme.palette.mode === "dark"
                    ? alpha(theme.palette.text.secondary, 0.45)
                    : theme.palette.divider,
                  backgroundColor: isLoved
                    ? theme.colors.red
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isLoved
                      ? alpha(theme.colors.red, 0.9)
                      : theme.palette.mode === "dark"
                      ? theme.palette.action.selected
                      : theme.palette.action.hover,
                    borderColor: isLoved
                      ? "transparent"
                      : theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.secondary, 0.65)
                      : theme.palette.text.secondary,
                  },
                })}
              >
                {isLoved ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
              </IconButton>
            )}

            {canShareFromDetails && (
              <IconButton
                onClick={handleShareFromDetails}
                aria-label={
                  canResharePending
                    ? "Reshare time capsule"
                    : isPrivateCapsule || isEditableForwardCapsule
                      ? "Forward time capsule"
                      : "Share time capsule"
                }
                sx={(theme) => ({
                  flex: "0 0 auto",
                  width: 48,
                  height: 48,
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.text.secondary, 0.45)
                      : theme.palette.divider,
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.colors.blue,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? theme.palette.action.hover
                      : "transparent",
                  "&:hover": {
                    borderColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.text.secondary, 0.65)
                        : theme.palette.text.secondary,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                  },
                })}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            )}

          </Box>
        </Container>
      </Box>

      {insiderSuccessOpen && insiderSuccessPayload && (
        <SuccessDialog
          trigger={insiderSuccessOpen}
          payload={insiderSuccessPayload}
          storageKey={insiderSuccessKey || undefined}
          onClose={() => setInsiderSuccessOpen(false)}
        />
      )}
    </>
  );
};

export default PinDetailsPage;
